import {
  ArrowLeftOutlined,
  BarChartOutlined,
  BulbOutlined,
  DatabaseOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { canAdminister, canModerate } from '@cult/domain';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Flex, Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { CountBadge } from '../../../../components';
import { useCurrentUser } from '../../../../state/use-current-user';
import { useMenuCounters } from '../../hooks';
import styles from './AdminShell.module.scss';

const { Sider, Content } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

// Ideas moderation is built inside the component so the pending count can badge it.
const PRAISES_MODERATION_ITEM: MenuItem = {
  key: 'praises-moderation',
  icon: <StarOutlined />,
  label: <Link to="/admin/praises">Praises</Link>,
};

const FEEDBACK_MODERATION_ITEM: MenuItem = {
  key: 'feedback-moderation',
  icon: <MessageOutlined />,
  label: <Link to="/admin/feedback">Direct feedback</Link>,
};

// Every reference list, one tab per type. Idea managers see it too — their only tab is Categories.
const REFERENCE_DATA_ITEM: MenuItem = {
  key: 'reference-data',
  icon: <DatabaseOutlined />,
  label: <Link to="/admin/reference">Reference data</Link>,
};

const STATISTICS_ITEM: MenuItem = {
  key: 'statistics',
  icon: <BarChartOutlined />,
  label: <Link to="/admin/statistics">Statistics</Link>,
};

const USERS_ITEM: MenuItem = {
  key: 'users',
  icon: <TeamOutlined />,
  label: <Link to="/admin/users">Users</Link>,
};

const ACCESS_ITEM: MenuItem = {
  key: 'access',
  icon: <SafetyCertificateOutlined />,
  label: <Link to="/admin/access">Access Control</Link>,
};

export const AdminShell = () => {
  const role = useCurrentUser((s) => s.user?.role);
  const isAdmin = !!role && canAdminister(role);
  // Idea managers moderate ideas only; every other item needs full moderator rights.
  const isModerator = !!role && canModerate(role);
  const { data: counters } = useMenuCounters();
  const ideasModerationItem: MenuItem = {
    key: 'ideas-moderation',
    icon: <BulbOutlined />,
    label: (
      <Flex align="center" justify="space-between" gap={8}>
        <Link to="/admin/ideas">Ideas</Link>
        <CountBadge count={counters?.pendingIdeas ?? 0} />
      </Flex>
    ),
  };
  const items: MenuItem[] = [
    {
      key: 'moderation-group',
      type: 'group',
      label: 'Moderation',
      children: [ideasModerationItem, ...(isModerator ? [PRAISES_MODERATION_ITEM, FEEDBACK_MODERATION_ITEM] : [])],
    },
    { type: 'divider' },
    REFERENCE_DATA_ITEM,
    ...(isModerator ? [STATISTICS_ITEM, USERS_ITEM] : []),
    ...(isAdmin ? [ACCESS_ITEM] : []),
  ];
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const selectedKey = (() => {
    if (pathname.startsWith('/admin/ideas')) return 'ideas-moderation';
    if (pathname.startsWith('/admin/reference')) return 'reference-data';
    if (pathname.startsWith('/admin/praises')) return 'praises-moderation';
    if (pathname.startsWith('/admin/feedback')) return 'feedback-moderation';
    if (pathname.startsWith('/admin/users')) return 'users';
    if (pathname.startsWith('/admin/market/orders')) return 'market-orders';
    if (pathname.startsWith('/admin/market')) return 'market-catalog';
    if (pathname.startsWith('/admin/access')) return 'access';
    if (pathname.startsWith('/admin/statistics')) return 'statistics';
    return 'ideas-moderation';
  })();

  return (
    <Layout style={{ minHeight: 'calc(100vh - 64px)', background: 'transparent' }}>
      <Sider width={232} theme="light" className={styles.sider} style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={styles.back}>
          <Link to="/home">
            <ArrowLeftOutlined />
            <span>Back to app</span>
          </Link>
        </div>
        <Menu mode="inline" selectedKeys={[selectedKey]} items={items} style={{ paddingTop: 12 }} />
      </Sider>
      <Content style={{ padding: '24px 32px' }}>
        <Outlet />
      </Content>
    </Layout>
  );
};
